import React, { useCallback, useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { findTourForPath } from '../lib/tourSteps';

/**
 * Floating "Take the tour" pill. Renders only when the current route has a
 * registered tour. Uses react-joyride v3 API.
 */
const TourController = ({ variant = 'admin' }) => {
  const { pathname } = useLocation();
  const [tour, setTour] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    const t = findTourForPath(pathname);
    setTour(t || null);
    setRunning(false);
  }, [pathname]);

  const start = useCallback(() => {
    if (!tour) return;
    setRunning(true);
  }, [tour]);

  const onCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunning(false);
      if (tour) {
        try {
          localStorage.setItem(`tour_seen_${tour.id}`, '1');
        } catch (_) { /* localStorage unavailable */ }
      }
    }
  };

  if (!tour) return null;

  const seen = (() => {
    try {
      return localStorage.getItem(`tour_seen_${tour.id}`) === '1';
    } catch (_) {
      return true;
    }
  })();

  return (
    <>
      <button
        onClick={start}
        data-testid={`tour-btn-${tour.id}`}
        className={
          'fixed z-40 right-6 group flex items-center gap-2 px-4 py-2.5 rounded-full ' +
          'bg-[#0f5c3d] hover:bg-[#0b4a30] text-white shadow-lg shadow-emerald-900/20 ' +
          'transition-all text-sm font-medium ' +
          (variant === 'books' ? 'bottom-24 lg:bottom-6' : 'bottom-20 lg:bottom-6')
        }
        aria-label={`Take the ${tour.title} tour`}
        style={running ? { display: 'none' } : {}}
      >
        <HelpCircle className="w-4 h-4" />
        <span className="hidden sm:inline">Take the tour</span>
        <span className="sm:hidden">Tour</span>
        {!seen && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border border-white animate-pulse" />
        )}
      </button>

      <Joyride
        steps={tour.steps}
        run={running}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        callback={onCallback}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          nextWithProgress: 'Next ({current}/{total})',
          skip: 'Skip tour',
        }}
        defaultOptions={{
          primaryColor: '#0f5c3d',
          textColor: '#0b1a2e',
          backgroundColor: '#ffffff',
          arrowColor: '#ffffff',
          overlayColor: 'rgba(11, 26, 46, 0.55)',
          zIndex: 10000,
        }}
        styles={{
          tooltip: {
            borderRadius: 12,
            padding: 20,
            maxWidth: 400,
            boxShadow: '0 12px 40px rgba(11, 26, 46, 0.25)',
          },
          tooltipTitle: {
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: 24,
            fontWeight: 500,
            color: '#0b1a2e',
            margin: 0,
            marginBottom: 6,
          },
          tooltipContent: {
            fontSize: 13.5,
            lineHeight: 1.55,
            color: '#334155',
            padding: '4px 0 12px',
          },
          buttonPrimary: {
            backgroundColor: '#0f5c3d',
            borderRadius: 999,
            padding: '9px 20px',
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
          },
          buttonBack: {
            color: '#0f5c3d',
            fontSize: 13,
            fontWeight: 500,
            marginRight: 8,
          },
          buttonSkip: {
            color: '#94a3b8',
            fontSize: 12,
          },
          buttonClose: {
            color: '#94a3b8',
          },
        }}
      />
    </>
  );
};

export default TourController;
